import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileDown, Plus, Wrench } from "lucide-react";
import { vehicleLabel } from "@/lib/vehicles";
import { ExternalServiceDialog, ExternalServiceCard, fmtTL } from "@/components/ExternalServiceDialog";

export const canEditExternal = (user, r) =>
  user?.role === "admin" || (user?.role === "data_entry" && r.entered_by === user?.id);

export default function ExternalServices() {
  const { user } = useAuth();
  const canAdd = ["admin", "data_entry"].includes(user?.role);
  const [vehicles, setVehicles] = useState([]);
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ vehicle_id: "", date_from: "", date_to: "" });
  const [dlg, setDlg] = useState({ open: false, record: null });

  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const load = useCallback(() => {
    api.get("/external-services", { params }).then((r) => setRows(r.data || [])).catch(() => toast.error("Yüklenemedi"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.vehicle_id, f.date_from, f.date_to]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {}); }, []);

  const del = async (r) => {
    if (!window.confirm("Bu dış servis kaydı silinsin mi?")) return;
    try { await api.delete(`/external-services/${r.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const exportPdf = async () => {
    try {
      const r = await api.get("/external-services/export.pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "YAZTUR-Dis-Servis.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF alınamadı"); }
  };

  const total = rows.reduce((s, r) => s + (Number(r.amount_tl) || 0), 0);

  return (
    <div className="space-y-6" data-testid="external-services-page">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-mono">Yönetim</div>
          <h1 className="mt-1 font-heading font-bold text-3xl lg:text-4xl tracking-tight text-slate-900">Dış Servis Kayıtları</h1>
          <p className="mt-2 text-sm text-slate-500">Dış serviste yapılan bakım/onarımlar. Prim ve havuz hesabını etkilemez.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportPdf} data-testid="es-export-pdf-btn"><FileDown className="w-4 h-4 mr-2" /> PDF</Button>
          {canAdd && (
            <Button onClick={() => setDlg({ open: true, record: null })} className="bg-[#1E3A8A] hover:bg-[#1e40af]" data-testid="es-add-btn">
              <Plus className="w-4 h-4 mr-2" /> Dış Servis Kaydı
            </Button>
          )}
        </div>
      </div>

      <Card className="border-slate-200 shadow-none">
        <CardContent className="pt-6 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider">Araç</Label>
            <select value={f.vehicle_id} onChange={(e) => setF({ ...f, vehicle_id: e.target.value })} data-testid="es-filter-vehicle"
              className="mt-1 w-full h-10 px-2 rounded-md border border-slate-200 bg-white text-sm font-mono">
              <option value="">Tüm araçlar</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{vehicleLabel(v.code, v.plate, v.brand)}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Başlangıç</Label>
            <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="mt-1" data-testid="es-filter-from" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Bitiş</Label>
            <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="mt-1" data-testid="es-filter-to" />
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-4 text-sm text-slate-600">
        <span data-testid="es-count"><Wrench className="w-4 h-4 inline mr-1" />{rows.length} kayıt</span>
        <span data-testid="es-total">Toplam: <b>{fmtTL(total)}</b></span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="es-list">
        {rows.length === 0 && <div className="text-slate-400 text-sm py-8">Kayıt bulunamadı</div>}
        {rows.map((r) => (
          <ExternalServiceCard key={r.id} r={r}
            onEdit={canEditExternal(user, r) ? (x) => setDlg({ open: true, record: x }) : null}
            onDelete={user?.role === "admin" ? del : null} />
        ))}
      </div>

      <ExternalServiceDialog open={dlg.open} record={dlg.record}
        onOpenChange={(o) => setDlg({ ...dlg, open: o })} onSaved={load} />
    </div>
  );
}
